import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RefreshCw, ShieldCheck, CircleCheck, CircleX, FlaskConical, RotateCcw, AlertTriangle, Mail, PieChart } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const brl = (c) => (c == null ? "—" : (c / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const dt = (v) => (v ? new Date(v.endsWith?.("Z") || v.includes?.("+") ? v : `${v}Z`).toLocaleString("pt-BR") : "—");

const PAY_STATUS = { "1": "Aguardando pagamento", "2": "Aprovada", "3": "Cancelada", "4": "Devolvida", "5": "Bloqueada", "6": "Completa", "7": "Abandono", "9": "Reembolso solicitado" };
const CONV_STATUS = {
  pending: { label: "Pendente", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  sending: { label: "Enviando", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  sent: { label: "Enviada", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  validated: { label: "Validada (teste)", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  error: { label: "Falha", cls: "bg-red-50 text-red-800 border-red-200" },
  verification_failed: { label: "Não confirmada na Monetizze", cls: "bg-red-50 text-red-800 border-red-200" },
  expired: { label: "Expirada (+7 dias)", cls: "bg-stone-100 text-stone-700 border-stone-200" },
};

function Check({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {ok ? <CircleCheck className="w-4 h-4 text-emerald-600" /> : <CircleX className="w-4 h-4 text-[#B9A48F]" />}
      <span className={ok ? "text-[#3A2921]" : "text-[#9C877A]"}>{label}</span>
    </li>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5DCCF] bg-white/70 p-4">
      <p className="text-[11px] uppercase tracking-widest text-[#9C877A]">{label}</p>
      <p data-testid={testid} className="font-serif-display text-2xl font-semibold text-[#3A2921] mt-1">{value}</p>
    </div>
  );
}

function ConfigCard({ status, onValidate, validating, validation }) {
  if (!status) return null;
  const mode = !status.send_enabled ? "Envio DESATIVADO" : status.validate_only ? "Envio ativo em MODO TESTE (validate_only)" : "Envio REAL ativo";
  return (
    <div className="rounded-2xl border border-[#E5DCCF] bg-white/70 p-5" data-testid="conversions-config-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#4C7A5A]" />
          <h2 className="font-semibold text-[#3A2921]">Configuração da integração</h2>
        </div>
        <span data-testid="conversions-send-mode" className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border ${status.send_enabled && !status.validate_only ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{mode}</span>
      </div>
      <ul className="mt-4 grid sm:grid-cols-2 gap-2">
        <Check ok={status.pixel_id_configured} label="Pixel ID do OpenAI Ads" />
        <Check ok={status.conversion_key_configured} label="Chave da API de Conversões" />
        <Check ok={status.monetizze_chave_unica_configured} label="chave_unica da Monetizze" />
        <Check ok={status.monetizze_api_token_configured} label="Token da API Monetizze (verificação)" />
        <Check ok={status.monetizze_webhook_token_configured} label="Token secreto do webhook" />
        <Check ok={status.product_filter.length > 0} label={`Filtro de produto ${status.product_filter.length ? `(${status.product_filter.join(", ")})` : "(opcional)"}`} />
      </ul>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button data-testid="conversions-validate-test-button" onClick={onValidate} disabled={validating}
          className="inline-flex items-center gap-2 rounded-xl bg-[#3A2921] text-[#FDFBF7] px-4 py-2 text-sm font-semibold hover:bg-[#5C4638] disabled:opacity-50 transition-colors">
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Testar validação da API (não registra conversão)
        </button>
        {status.last_validation && (
          <span data-testid="conversions-last-validation" className="text-xs text-[#6E5A4F]">
            Último teste: {status.last_validation.ok ? "OK" : "falhou"} (HTTP {status.last_validation.http_status}) em {dt(status.last_validation.at)}
          </span>
        )}
      </div>
      {validation && (
        <pre data-testid="conversions-validate-result" className="mt-3 text-xs bg-[#F4EFE6] rounded-xl p-3 overflow-auto max-h-48 text-[#3A2921]">{JSON.stringify(validation, null, 2)}</pre>
      )}
    </div>
  );
}

const FAILED = ["error", "verification_failed", "expired"];

function FailureAlert({ orders, onRetry }) {
  const failed = orders.filter((o) => o.conversao && FAILED.includes(o.conversao.status));
  if (!failed.length) return null;
  return (
    <div data-testid="conversions-failure-alert" role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5">
      <div className="flex items-center gap-2 text-red-800">
        <AlertTriangle className="w-5 h-5" />
        <h2 className="font-semibold">{failed.length} conversão(ões) com falha no envio ao OpenAI Ads</h2>
      </div>
      <ul className="mt-3 space-y-2">
        {failed.map((o) => {
          const c = o.conversao;
          const canRetry = c.status !== "expired";
          return (
            <li key={c.event_id} data-testid={`failure-item-${o.codigo_venda}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/70 border border-red-100 px-3 py-2 text-sm">
              <span className="text-red-900">
                Venda <span className="font-mono">{o.codigo_venda}</span> · {CONV_STATUS[c.status]?.label} · {c.attempts} tentativa(s)
                {c.last_error && <span className="block text-xs text-red-700 break-words">{c.last_error}</span>}
              </span>
              {canRetry ? (
                <button data-testid={`failure-retry-${o.codigo_venda}`} onClick={() => onRetry(c.event_id, o.codigo_venda)} className="inline-flex items-center gap-1 rounded-full border border-red-300 px-3 py-1 text-xs font-semibold text-red-800 hover:bg-red-100 transition-colors">
                  <RotateCcw className="w-3.5 h-3.5" /> Reenviar
                </button>
              ) : <span className="text-xs text-red-700">Não reenviável (mais de 7 dias)</span>}
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-red-800/80">O reenvio usa sempre o mesmo identificador (monetizze_código), então a OpenAI descarta duplicatas.</p>
    </div>
  );
}

function SalesBySource() {
  const [period, setPeriod] = useState("30d");
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get(`/admin/reports/sales-by-source?period=${period}`).then((r) => setRep(r.data)).catch(() => setRep(null));
  }, [period]);
  const total = rep?.total_faturamento_centavos || 0;
  return (
    <div data-testid="sales-by-source-report" className="rounded-2xl border border-[#E5DCCF] bg-white/70 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><PieChart className="w-5 h-5 text-[#C5A059]" /><h2 className="font-semibold text-[#3A2921]">Vendas aprovadas por origem</h2></div>
        <select data-testid="sales-by-source-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-lg border border-[#E5DCCF] bg-white px-3 py-1.5 text-sm">
          <option value="7d">Últimos 7 dias</option><option value="30d">Últimos 30 dias</option><option value="90d">Últimos 90 dias</option><option value="all">Todo o período</option>
        </select>
      </div>
      {!rep || !rep.por_origem.length ? (
        <p data-testid="sales-by-source-empty" className="text-sm text-[#9C877A] py-6 text-center">Nenhuma compra aprovada no período.</p>
      ) : (
        <div className="mt-4 grid lg:grid-cols-2 gap-6">
          <ul className="space-y-3">
            {rep.por_origem.map((r) => {
              const pct = total ? Math.round((r.faturamento_centavos / total) * 100) : 0;
              return (
                <li key={r.origem} data-testid={`source-row-${r.origem}`}>
                  <div className="flex justify-between text-sm"><span className="font-medium">{r.origem}</span><span>{r.compras} compra(s) · {brl(r.faturamento_centavos)}</span></div>
                  <div className="h-2 rounded-full bg-[#F4EFE6] mt-1 overflow-hidden"><div className="h-full bg-[#C5A059] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
                </li>
              );
            })}
            <li className="pt-2 border-t border-[#F0E8DC] text-sm font-semibold flex justify-between"><span>Total</span><span data-testid="sales-by-source-total">{rep.total_compras} compra(s) · {brl(total)}</span></li>
          </ul>
          <table className="w-full text-sm" data-testid="sales-by-campaign-table">
            <thead className="text-left text-[11px] uppercase tracking-widest text-[#9C877A]"><tr><th className="py-1">Origem</th><th>Campanha</th><th>Mídia</th><th className="text-right">Compras</th><th className="text-right">Faturamento</th></tr></thead>
            <tbody>
              {rep.por_campanha.map((r, i) => (
                <tr key={i} className="border-t border-[#F0E8DC]"><td className="py-1.5">{r.origem}</td><td>{r.campanha}</td><td>{r.utm_medium || "—"}</td><td className="text-right">{r.compras}</td><td className="text-right">{brl(r.faturamento_centavos)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-[#9C877A]">Origem definida só por dados recebidos: identificador do ChatGPT Ads (oppref), utm_source, gclid ou fbclid devolvidos pela Monetizze. Período pela data de aprovação da venda. Sem esses dados, a venda aparece como “Origem não identificada”. Faturamento = valor bruto das vendas aprovadas/completas.</p>
    </div>
  );
}

function PrivacyEmailCard() {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/privacy-email").then((r) => setEmail(r.data.email || "")).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/privacy-email", { email });
      toast.success(email ? "E-mail de privacidade salvo" : "E-mail de privacidade removido");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="privacy-email-card" className="rounded-2xl border border-[#E5DCCF] bg-white/70 p-5">
      <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#C5A059]" /><h2 className="font-semibold text-[#3A2921]">Contato LGPD na Política de Privacidade</h2></div>
      <div className="mt-3 flex flex-wrap gap-2">
        <input data-testid="privacy-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="privacidade@seudominio.com.br" className="flex-1 min-w-[220px] rounded-xl border border-[#E5DCCF] bg-white px-3 py-2 text-sm" />
        <button data-testid="privacy-email-save" onClick={save} disabled={saving} className="rounded-xl bg-[#3A2921] text-[#FDFBF7] px-4 py-2 text-sm font-semibold hover:bg-[#5C4638] disabled:opacity-50 transition-colors">{saving ? "Salvando..." : "Salvar"}</button>
      </div>
      <p className="mt-2 text-xs text-[#9C877A]">Aparece em /privacidade. Deixe vazio para usar a orientação “responda ao e-mail de confirmação da compra”.</p>
    </div>
  );
}

function OrdersTable({ orders, onRetry }) {
  if (!orders.length) {
    return <p data-testid="orders-empty" className="text-sm text-[#9C877A] py-10 text-center">Nenhuma venda recebida pelo webhook ainda.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E5DCCF] bg-white/70">
      <table className="w-full text-sm" data-testid="orders-table">
        <thead className="text-left text-[11px] uppercase tracking-widest text-[#9C877A] border-b border-[#E5DCCF]">
          <tr><th className="p-3">Venda</th><th className="p-3">Valor</th><th className="p-3">Pagamento</th><th className="p-3">Atualizada</th><th className="p-3">Conversão</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const c = o.conversao;
            const st = c ? CONV_STATUS[c.status] || { label: c.status, cls: "" } : null;
            return (
              <tr key={o.codigo_venda} data-testid={`order-row-${o.codigo_venda}`} className="border-b border-[#F0E8DC] last:border-0 align-top">
                <td className="p-3 font-mono text-xs">{o.codigo_venda}</td>
                <td className="p-3">{brl(o.valor_centavos)}</td>
                <td className="p-3">{PAY_STATUS[o.status_codigo] || o.status || o.status_codigo}</td>
                <td className="p-3 text-xs text-[#6E5A4F]">{dt(o.updated_at)}</td>
                <td className="p-3">
                  {st ? (
                    <div className="space-y-1">
                      <span className={`inline-block text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${st.cls}`}>{st.label}</span>
                      <p className="text-[11px] text-[#9C877A]">{c.attempts} tentativa(s) · {c.has_attribution ? "com atribuição" : "sem atribuição do Pixel"}</p>
                      {c.last_error && <p className="text-[11px] text-red-700 max-w-xs break-words">{c.last_error}</p>}
                    </div>
                  ) : <span className="text-xs text-[#B9A48F]">Não se aplica</span>}
                </td>
                <td className="p-3">
                  {c && ["error", "validated", "verification_failed"].includes(c.status) && (
                    <button data-testid={`order-retry-${o.codigo_venda}`} onClick={() => onRetry(c.event_id)} className="inline-flex items-center gap-1 text-xs text-[#8a6d2f] hover:underline">
                      <RotateCcw className="w-3.5 h-3.5" /> Reenviar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminConversions() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState({ orders: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([api.get("/admin/conversions/status"), api.get("/admin/orders")]);
      setStatus(s.data);
      setData(o.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const r = await api.post("/admin/conversions/validate-test");
      setValidation(r.data);
      r.data.ok ? toast.success("Validação aceita pela OpenAI (nada foi registrado)") : toast.error(`OpenAI respondeu HTTP ${r.data.http_status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setValidating(false);
    }
  };

  const retry = async (eventId, codigo) => {
    if (codigo && !window.confirm(`Reenviar a conversão da venda ${codigo}? O mesmo identificador será usado, sem duplicar.`)) return;
    try {
      await api.post(`/admin/conversions/${eventId}/retry`);
      toast.success("Conversão recolocada na fila");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const counts = status?.counts || {};
  const s = data.summary || {};
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-[#E5DCCF]">
          <div>
            <Link to="/admin" data-testid="conversions-back-link" className="inline-flex items-center gap-1 text-sm text-[#6E5A4F] hover:text-[#3A2921]"><ArrowLeft className="w-4 h-4" /> Voltar ao painel</Link>
            <h1 data-testid="conversions-title" className="font-cinzel uppercase tracking-[0.15em] text-lg text-[#3A2921] mt-2">Vendas & Conversões — OpenAI Ads</h1>
          </div>
          <button data-testid="conversions-refresh-button" onClick={load} className="inline-flex items-center gap-2 text-sm text-[#6E5A4F] hover:text-[#3A2921]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat label="Vendas recebidas" value={s.vendas_recebidas ?? 0} testid="stat-orders" />
          <Stat label="Aprovadas" value={s.vendas_aprovadas ?? 0} testid="stat-approved" />
          <Stat label="Valor aprovado" value={brl(s.valor_aprovado_centavos || 0)} testid="stat-amount" />
          <Stat label="Conv. enviadas" value={(counts.sent || 0) + (counts.validated || 0)} testid="stat-sent" />
          <Stat label="Conv. pendentes" value={(counts.pending || 0) + (counts.sending || 0)} testid="stat-pending" />
          <Stat label="Conv. com falha" value={(counts.error || 0) + (counts.verification_failed || 0) + (counts.expired || 0)} testid="stat-failed" />
        </div>

        <FailureAlert orders={data.orders || []} onRetry={retry} />
        <ConfigCard status={status} onValidate={validate} validating={validating} validation={validation} />
        <SalesBySource />
        <OrdersTable orders={data.orders || []} onRetry={retry} />
        <PrivacyEmailCard />
        <p className="text-xs text-[#9C877A]">Nenhum e-mail, telefone, CPF, endereço ou cookie é exibido ou armazenado em texto. “Validada (teste)” significa que o evento foi aceito em modo validate_only e não foi registrado pela OpenAI.</p>
      </div>
    </div>
  );
}
